import { useEffect, useRef, type ReactNode } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ isOpen, onClose, title, children, footer }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    function handleClose() {
      onClose();
    }
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === dialogRef.current) {
      onClose();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      className="border-none rounded-xl p-0 max-w-[480px] w-[90vw] shadow-xl backdrop:bg-black/50 backdrop:backdrop-blur-sm"
    >
      {/* Header */}
      <header className="flex justify-between items-center px-5 py-3.5 border-b border-border">
        <h2 className="m-0 text-base font-semibold text-text-primary">{title}</h2>
        <button
          onClick={onClose}
          aria-label="Close dialog"
          className="flex items-center justify-center h-7 w-7 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-page hover:text-text-primary transition-all"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      {/* Body */}
      <div className="px-5 py-4">
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <footer className="flex justify-end gap-2.5 px-5 py-3.5 border-t border-border bg-page/50 rounded-b-xl">
          {footer}
        </footer>
      )}
    </dialog>
  );
}
